import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useEmployeePortalAccess,
  useReleaseEmployeePortalAccess,
} from "@/queries/preboarding/use-preboarding.query.ts";
import { PortalAccessMode } from "@/types/employees/employee.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Modal,
  ModalProps,
  Stack,
  Text,
  Textarea,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface Props extends ModalProps {
  employeeId: number;
}

export function EmployeePortalAccessReleaseModal({ ...props }: Props) {
  const { employeeId, id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { showInfo } = useToasts();
  const releasePortalAccess = useReleaseEmployeePortalAccess(employeeId);
  const { data: portalAccess } = useEmployeePortalAccess(employeeId);

  const isBeforeFirstDay = portalAccess?.mode === PortalAccessMode.preboarding;

  const required = t("errors:field_required");

  const schema = z.object({
    reason: z.string({ error: required }).trim().min(1, required),
  });

  type Schema = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { reason: "" },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await releasePortalAccess.mutateAsync({ reason: data.reason });
      showInfo({ title: t("core:portal_access_release_success") });
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:portal_access_release")}
      variant={"middle"}
      layout={"base"}
      size={"md"}
      withCloser={true}
      actions={
        <Stack gap={"smd"}>
          <Button isLoading={releasePortalAccess.isPending} onClick={onSubmit} size={"lg"}>
            {t("core:portal_access_release_submit")}
          </Button>
          <Button size={"md"} variant={"plain"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Text>
            {isBeforeFirstDay
              ? t("core:portal_access_release_hint_preboarding")
              : t("core:portal_access_release_hint")}
          </Text>
          <Textarea
            label={t("core:reason")}
            error={errors.reason?.message}
            {...register("reason")}
          />
          <ServerErrorField errors={errors} />
        </Stack>
      </form>
    </Modal>
  );
}
